import React from 'react';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Badge, OverlayTrigger, Form } from 'react-bootstrap';
import {
    Calendar as CalendarIcon,
    MoreVertical,
    Plus,
    Tag,
    Trash2,
    Bell,
    Flag,
    Repeat,
    User
} from 'lucide-react';
import TaskStatusPopover, { STATUS_CONFIG } from './Popovers/TaskStatusPopover';
import TaskPriorityPopover from './Popovers/TaskPriorityPopover';
import TaskDueDatePopover from './Popovers/TaskDueDatePopover';
import TaskReminderPopover from './Popovers/TaskReminderPopover';
import TaskRecurrencePopover from './Popovers/TaskRecurrencePopover';
import { DeleteRecurringTaskDialog } from './DeleteRecurringTaskDialog';
import TaskTagsPopover from './Popovers/TaskTagsPopover';
import TaskAssigneePopover from './Popovers/TaskAssigneePopover';

export const SortableTaskCard = (props) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: props.task.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.3 : 1,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <TaskCard {...props} />
        </div>
    );
};

const getRecurrenceLabel = (rule) => {
    if (!rule) return null;
    if (rule.includes('FREQ=DAILY')) return 'Daily';
    if (rule.includes('FREQ=WEEKLY')) return 'Weekly';
    if (rule.includes('FREQ=MONTHLY')) return 'Monthly';
    if (rule.includes('FREQ=YEARLY')) return 'Yearly';
    return 'Recurring';
};

export const TaskCard = ({ task, completed, onCreateSubtask, viewMode = 'card', allTags = [], onAssignTag, onRemoveTag, onDeleteTask, onAddReminder, onDeleteReminder, onUpdateTask, taskStatuses = [], members = [], onIndent, onOutdent, onMoveUp, onMoveDown, dragProjection }) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const [content, setContent] = React.useState(task?.content || "");
    const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
    const [isEditingDescription, setIsEditingDescription] = React.useState(false);
    const [description, setDescription] = React.useState(task?.description || "");

    React.useEffect(() => {
        if (task) {
            setContent(task.content);
            setDescription(task.description || "");
        }
    }, [task?.content, task?.description]);

    if (!task) return null; // Safety

    // Is this task the projected indent target? (a task being dragged right will become our child)
    const isIndentTarget = viewMode === 'list' && dragProjection?.type === 'indent' && dragProjection.targetParentId === task.id;

    // Helper to call generic update
    const handleUpdate = (updates) => {
        if (onUpdateTask) {
            onUpdateTask(task.id, updates);
        }
    };

    const getStatusId = (switchType) => {
        if (!taskStatuses || taskStatuses.length === 0) return null;

        const sorted = [...taskStatuses].sort((a, b) => a.position - b.position);

        // Find default "Open" status (lowest position, not completed)
        const defaultOpen = sorted.find(s => !s.is_completed);

        // Find default "Done" status (lowest position, is completed, but prefer one named "Done" if exists to be safe, or just first completed)
        // Actually, usually the first completed status is "Done".
        const defaultDone = sorted.find(s => s.is_completed);

        if (switchType === 'TO_COMPLETED') {
            return defaultDone ? defaultDone.id : null;
        } else if (switchType === 'TO_OPEN') {
            // If we have a stored previous status that is open, we could use that?
            // For now, default to first open status (Backlog/Not Started)
            return defaultOpen ? defaultOpen.id : null;
        }

        return defaultOpen ? defaultOpen.id : null;
    };

    // Toggle logic now internal
    // Toggle logic now internal
    const handleToggle = () => {
        const newIsCompleted = !task.is_completed;
        // Logic: if becoming completed -> find best completed status.
        // If becoming open -> find best open status.
        const statusId = getStatusId(newIsCompleted ? 'TO_COMPLETED' : 'TO_OPEN');

        const updates = {
            isCompleted: newIsCompleted,
            statusId: statusId
        };
        handleUpdate(updates);
    };

    // Priority Colors
    const getPriorityClass = (p) => {
        if (!task) return '';
        if (p >= 3) return 'text-danger fw-bold';
        if (p === 2) return 'text-warning fw-bold';
        return 'text-muted';
    };

    const handlePriorityChange = (level) => {
        handleUpdate({ priority: level });
        document.body.click(); // Close popover
    };

    // Removed handleStatusChange as it is now handled by Popover directly calling onUpdate with logic.
    // Wait, Popover calls onUpdate, but TaskCard passes `handleUpdate` which wraps `onUpdateTask`.
    // Popover now returns full object { statusId, isCompleted }.
    // So we don't need a local handleStatusChange unless we used it for other things. 
    // It seems unused by other components except the popover trigger which we will update.

    const taskTags = task.tags || [];

    const handleSaveContent = () => {
        if (content.trim() && content !== task.content) {
            handleUpdate({ content });
        }
        setIsEditing(false);
    };

    const handleSaveDescription = () => {
        if (description !== (task.description || "")) {
            handleUpdate({ description: description || null });
        }
        setIsEditingDescription(false);
    };

    const handleDeleteClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (task.recurrence_rule) {
            setShowDeleteDialog(true);
        } else {
            if (window.confirm("Are you sure you want to delete this task?")) {
                onDeleteTask(task.id);
            }
        }
    };

    const handleDeleteInstance = () => {
        onDeleteTask(task.id, 'instance');
        setShowDeleteDialog(false);
    };

    const handleDeleteSeries = () => {
        onDeleteTask(task.id, 'series');
        setShowDeleteDialog(false);
    };

    const handleAssign = (userId) => {
        handleUpdate({ assignedTo: userId });
        document.body.click(); // Close popover
    };

    const renderAssigneeTrigger = () => {
        const assignedMember = members.find(m => m.id === task.assigned_to); // Match by Profile ID

        return (
            <OverlayTrigger
                trigger="click"
                placement="bottom"
                overlay={<TaskAssigneePopover members={members} assignedTo={task.assigned_to} onAssign={handleAssign} />}
                rootClose
                popperConfig={{ strategy: 'fixed' }}
            >
                <div
                    className="d-flex align-items-center justify-content-center rounded-circle border cursor-pointer bg-white"
                    style={{ width: 24, height: 24, overflow: 'hidden' }}
                    title={assignedMember ? `Assigned to ${assignedMember.displayName}` : 'Assign Member'}
                    onClick={(e) => { e.stopPropagation(); }}
                >
                    {assignedMember ? (
                        assignedMember.avatarUrl ? (
                            <img src={assignedMember.avatarUrl} alt={assignedMember.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            <div className="w-100 h-100 d-flex align-items-center justify-content-center text-white" style={{ backgroundColor: assignedMember.color || '#6c757d', fontSize: '10px', fontWeight: 'bold' }}>
                                {assignedMember.displayName?.substring(0, 2).toUpperCase()}
                            </div>
                        )
                    ) : (
                        <User size={14} className="text-muted opacity-50" />
                    )}
                </div>
            </OverlayTrigger>
        );
    };

    const handleListKeyDown = (e) => {
        // Don't intercept when the user is editing the task content
        if (isEditing) return;

        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault();
            if (onIndent) onIndent(task.id);
        } else if (e.key === 'Tab' && e.shiftKey) {
            e.preventDefault();
            if (onOutdent) onOutdent(task.id);
        } else if (e.key === 'ArrowUp' && e.altKey) {
            e.preventDefault();
            if (onMoveUp) onMoveUp(task.id);
        } else if (e.key === 'ArrowDown' && e.altKey) {
            e.preventDefault();
            if (onMoveDown) onMoveDown(task.id);
        }
    };

    if (viewMode === 'list') {
        return (
            <React.Fragment>
                <div
                    className={`task-list-item p-3 border-bottom bg-white d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between ${completed ? 'opacity-50' : ''}`}
                    style={{ cursor: 'default', transition: 'background-color 0.2s' }}
                    tabIndex={completed ? undefined : 0}
                    onKeyDown={completed ? undefined : handleListKeyDown}
                >
                    <div className="d-flex align-items-center gap-3 flex-grow-1 w-100">
                        <div
                            className={`rounded-circle border d-flex align-items-center justify-content-center ${completed ? 'bg-success border-success' : 'border-secondary'}`}
                            style={{ width: '20px', height: '20px', minWidth: '20px', cursor: 'pointer' }}
                            onClick={handleToggle}
                        >
                            {completed && <span className="text-white" style={{ fontSize: '10px' }}>✓</span>}
                        </div>

                        <div className="d-flex flex-column flex-grow-1">

                            <div className="d-flex align-items-center gap-2">
                                {isEditing ? (
                                    <Form.Control
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}
                                        onBlur={handleSaveContent}
                                        onKeyDown={(e) => {
                                            // Stop propagation to prevent dnd-kit from picking up Space/Enter
                                            e.stopPropagation();
                                            if (e.key === 'Enter') handleSaveContent();
                                            if (e.key === 'Escape') {
                                                setContent(task.content);
                                                setIsEditing(false);
                                            }
                                        }}
                                        autoFocus
                                        size="sm"
                                        className="border-0 p-0 shadow-none bg-transparent fw-medium"
                                        style={{ width: '100%' }}
                                    />
                                ) : (
                                    <span
                                        className={`fw-medium flex-grow-1 ${completed ? 'text-decoration-line-through text-muted' : ''}`}
                                        onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
                                        title="Click to edit task"
                                        style={{ cursor: 'text' }}
                                    >
                                        {task.content}
                                    </span>
                                )}
                            </div>

                            {/* Tags below title */}
                            {taskTags.length > 0 && (
                                <div className="d-flex flex-wrap gap-1 mt-1">
                                    {taskTags.map(tag => (
                                        <Badge key={tag.id} bg="light" text="dark" className="border text-muted fw-normal" style={{ fontSize: '0.65rem' }}>
                                            {tag.name}
                                        </Badge>
                                    ))}
                                </div>
                            )}

                            {/* Description - inline editable */}
                            {!completed && isEditingDescription ? (
                                <Form.Control
                                    as="textarea"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    onBlur={handleSaveDescription}
                                    onKeyDown={(e) => {
                                        e.stopPropagation();
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSaveDescription();
                                        }
                                        if (e.key === 'Escape') {
                                            setDescription(task.description || "");
                                            setIsEditingDescription(false);
                                        }
                                    }}
                                    autoFocus
                                    size="sm"
                                    rows={2}
                                    placeholder="Add a description..."
                                    className="border-0 p-0 shadow-none bg-transparent small text-muted mt-1 resize-none"
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <div
                                    className={`small text-muted mt-0 ${!completed ? 'cursor-pointer' : ''}`}
                                    onClick={!completed ? (e) => { e.stopPropagation(); setIsEditingDescription(true); } : undefined}
                                    title={!completed ? (task.description ? "Click to edit description" : "Click to add description") : undefined}
                                >
                                    {task.description ? (
                                        <span className="text-truncate d-block" style={{ maxWidth: '500px' }}>{task.description}</span>
                                    ) : (
                                        !completed && <span className="text-muted opacity-50 fst-italic task-add-description-hint" style={{ fontSize: '0.75rem' }}>+ Add description</span>
                                    )}
                                </div>
                            )}

                            {task.due_date && (
                                <div className="d-flex align-items-center gap-2 small text-muted">
                                    <div className={`d-flex align-items-center gap-1 ${new Date(task.due_date) < new Date().setHours(0, 0, 0, 0) ? 'text-danger' : ''}`}>
                                        <CalendarIcon size={12} />
                                        <span>{new Date(task.due_date).toLocaleDateString()}</span>
                                        {task.recurrence_rule && <span className="ms-1 text-muted fst-italic" style={{ fontSize: '0.7em' }}>({getRecurrenceLabel(task.recurrence_rule)})</span>}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-3 mt-2 mt-sm-0 w-100 w-sm-auto justify-content-between justify-content-sm-end">
                        {/* Assignee Trigger */}
                        <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
                            {renderAssigneeTrigger()}
                        </div>

                        {/* Priority Indicator for List */}
                        <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
                            <OverlayTrigger trigger="click" placement="bottom" overlay={<TaskPriorityPopover task={task} onUpdate={handleUpdate} />} rootClose popperConfig={{ strategy: 'fixed' }}>
                                <Button variant="link" className="p-0 text-decoration-none" title="Set Priority">
                                    {task.priority >= 2 ? (
                                        <span className={`small ${getPriorityClass(task.priority)}`}>{task.priority >= 3 ? 'High' : 'Med'}</span>
                                    ) : (
                                        <Flag size={16} className={task.priority === 1 ? 'text-primary' : 'text-muted opacity-25'} />
                                    )}
                                </Button>
                            </OverlayTrigger>
                        </div>

                        <div className="d-flex align-items-center gap-2" onClick={e => e.stopPropagation()}>
                            {/* Status Trigger */}
                            <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} className="d-inline-block">
                                <OverlayTrigger trigger="click" placement="bottom" overlay={<TaskStatusPopover task={task} taskStatuses={taskStatuses} onUpdate={handleUpdate} />} rootClose popperConfig={{ strategy: 'fixed' }}>
                                    <Button variant="link" className="p-0 text-decoration-none" title="Set Status">
                                        {(() => {
                                            const statusObj = taskStatuses.find(s => s.id === task.status_id);
                                            const statusName = statusObj ? statusObj.name : (task.status_name || 'Not Started');
                                            const color = statusObj ? statusObj.color : 'text-muted';

                                            const upper = statusName.toUpperCase().replace(' ', '_');
                                            const config = STATUS_CONFIG[upper] || STATUS_CONFIG['NOT_STARTED'];
                                            const Icon = config.icon;

                                            return <Icon size={16} className={color} />;
                                        })()}
                                    </Button>
                                </OverlayTrigger>
                            </div>

                            {/* Due Date Trigger */}
                            {!completed && (
                                <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} className="d-inline-block">
                                    <OverlayTrigger trigger="click" placement="bottom" overlay={<TaskDueDatePopover task={task} onUpdate={handleUpdate} />} rootClose popperConfig={{ strategy: 'fixed' }}>
                                        <Button
                                            variant="link"
                                            className={`p-0 ${task.due_date ? (new Date(task.due_date) < new Date() ? 'text-danger' : 'text-primary') : 'text-muted'} opacity-50 hover-opacity-100`}
                                            title="Set Due Date"
                                        >
                                            <CalendarIcon size={16} fill={task.due_date ? 'currentColor' : 'none'} />
                                        </Button>
                                    </OverlayTrigger>
                                </div>
                            )}

                            {/* Reminder Trigger */}
                            {!completed && (
                                <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} className="d-inline-block">
                                    <OverlayTrigger trigger="click" placement="bottom" overlay={<TaskReminderPopover task={task} onAddReminder={onAddReminder} onDeleteReminder={onDeleteReminder} />} rootClose popperConfig={{ strategy: 'fixed' }}>
                                        <Button
                                            variant="link"
                                            className={`p-0 ${task.reminders?.length > 0 ? 'text-primary' : 'text-muted'} opacity-50 hover-opacity-100`}
                                            title="Set Reminder"
                                        >
                                            <Bell size={16} fill={task.reminders?.length > 0 ? 'currentColor' : 'none'} />
                                        </Button>
                                    </OverlayTrigger>
                                </div>
                            )}

                            {/* Recurrence Trigger */}
                            {!completed && (
                                <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} className="d-inline-block">
                                    <OverlayTrigger trigger="click" placement="bottom" overlay={<TaskRecurrencePopover task={task} onUpdate={handleUpdate} />} rootClose popperConfig={{ strategy: 'fixed' }}>
                                        <Button
                                            variant="link"
                                            className={`p-0 ${task.recurrence_rule ? 'text-primary' : 'text-muted'} opacity-50 hover-opacity-100`}
                                            title="Set Recurrence"
                                        >
                                            <Repeat size={16} />
                                        </Button>
                                    </OverlayTrigger>
                                </div>
                            )}
                            {/* Tag Trigger */}
                            {!completed && (
                                <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} className="d-inline-block">
                                    <OverlayTrigger trigger="click" placement="bottom" overlay={<TaskTagsPopover task={task} allTags={allTags} onAssignTag={onAssignTag} onRemoveTag={onRemoveTag} />} rootClose popperConfig={{ strategy: 'fixed' }}>
                                        <Button
                                            variant="link"
                                            className="p-0 text-muted opacity-50 hover-opacity-100"
                                            title="Manage Tags"
                                        >
                                            <Tag size={16} />
                                        </Button>
                                    </OverlayTrigger>
                                </div>
                            )}

                            {!completed && (
                                <Button
                                    variant="link"
                                    className="p-0 text-muted opacity-50 hover-opacity-100"
                                    onClick={(e) => { e.stopPropagation(); onCreateSubtask(task.id); }}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    title="Add Subtask"
                                >
                                    <Plus size={18} />
                                </Button>
                            )}

                            {/* Delete Button */}
                            {!completed && (
                                <Button
                                    variant="link"
                                    className="p-0 text-muted opacity-50 hover-danger"
                                    onClick={handleDeleteClick}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    title="Delete Task"
                                >
                                    <Trash2 size={16} />
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Render Subtasks in List View — sortable within parent */}
                {((task.children && task.children.length > 0) || isIndentTarget) && (
                    <div className="ps-4 ms-2">
                        <SortableContext
                            items={(task.children || [])
                                .filter(c => !c.is_completed)
                                .map(c => c.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            {(task.children || []).map(child => (
                                child.is_completed ? (
                                    <TaskCard
                                        key={child.id}
                                        task={child}
                                        viewMode="list"
                                        onCreateSubtask={onCreateSubtask}
                                        completed={child.is_completed}
                                        allTags={allTags}
                                        onAssignTag={onAssignTag}
                                        onRemoveTag={onRemoveTag}
                                        onDeleteTask={onDeleteTask}
                                        onUpdateTask={onUpdateTask}
                                        onAddReminder={onAddReminder}
                                        onDeleteReminder={onDeleteReminder}
                                        taskStatuses={taskStatuses}
                                        members={members}
                                    />
                                ) : (
                                    <SortableTaskCard
                                        key={child.id}
                                        task={child}
                                        viewMode="list"
                                        onCreateSubtask={onCreateSubtask}
                                        completed={child.is_completed}
                                        allTags={allTags}
                                        onAssignTag={onAssignTag}
                                        onRemoveTag={onRemoveTag}
                                        onDeleteTask={onDeleteTask}
                                        onUpdateTask={onUpdateTask}
                                        onAddReminder={onAddReminder}
                                        onDeleteReminder={onDeleteReminder}
                                        taskStatuses={taskStatuses}
                                        members={members}
                                        onIndent={onIndent}
                                        onOutdent={onOutdent}
                                        onMoveUp={onMoveUp}
                                        onMoveDown={onMoveDown}
                                    />
                                )
                            ))}
                        </SortableContext>
                        {/* Indent drop preview: shown at bottom of target parent's children */}
                        {isIndentTarget && (
                            <div className="drop-indicator-line" style={{ marginLeft: 0 }}>
                                <div className="drop-indicator-bar" />
                                <span className="drop-indicator-label">{dragProjection.label}</span>
                            </div>
                        )}
                    </div>
                )}

                <DeleteRecurringTaskDialog
                    show={showDeleteDialog}
                    onHide={() => setShowDeleteDialog(false)}
                    onDeleteInstance={handleDeleteInstance}
                    onDeleteSeries={handleDeleteSeries}
                />
            </React.Fragment >
        );
    }

    // Default Card View
    return (
        <div className={`task-card ${completed ? 'completed' : ''}`} onClick={handleToggle}>
            {/* Priority Bar Clickable */}
            <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
                <OverlayTrigger trigger="click" placement="top" overlay={<TaskPriorityPopover task={task} onUpdate={handleUpdate} />} rootClose popperConfig={{ strategy: 'fixed' }}>
                    <div
                        className={`task-priority-indicator ${task.priority >= 3 ? 'task-priority-high' : task.priority === 2 ? 'task-priority-medium' : 'task-priority-low'}`}
                        style={{ cursor: 'pointer' }}
                        title="Change Priority"
                    ></div>
                </OverlayTrigger>
            </div>

            <div className="task-card-header mt-2">
                <div className="task-title-container flex-grow-1">
                    {isEditing ? (
                        <Form.Control
                            as="textarea"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            onBlur={handleSaveContent}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSaveContent();
                                }
                                if (e.key === 'Escape') {
                                    setContent(task.content);
                                    setIsEditing(false);
                                }
                            }}
                            autoFocus
                            size="sm"
                            className="border-0 p-0 shadow-none bg-transparent fw-medium resize-none"
                            style={{ width: '100%', minHeight: '24px' }}
                        />
                    ) : (
                        <div
                            className={`task-title ${completed ? 'text-decoration-line-through text-muted' : ''}`}
                            onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
                            title="Click to edit"
                        >
                            {task.content}
                        </div>
                    )}
                </div>
                {/* Card View Tag Trigger */}
                {!completed && (
                    <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
                        <OverlayTrigger trigger="click" placement="bottom" overlay={<TaskTagsPopover task={task} allTags={allTags} onAssignTag={onAssignTag} onRemoveTag={onRemoveTag} />} rootClose popperConfig={{ strategy: 'fixed' }}>
                            <MoreVertical size={16} className="text-muted opacity-50 cursor-pointer" />
                        </OverlayTrigger>
                    </div>
                )}
            </div>

            {task.description && (
                <div className="task-description mb-2">
                    {task.description}
                </div>
            )}

            {/* Tags in Card View */}
            {taskTags.length > 0 && (
                <div className="d-flex flex-wrap gap-1 mb-2">
                    {taskTags.map(tag => (
                        <Badge key={tag.id} bg="light" text="dark" className="border text-muted fw-normal" style={{ fontSize: '0.65rem' }}>
                            {tag.name}
                        </Badge>
                    ))}
                </div>
            )}

            <div className="task-content">
                {/* Subtasks Preview */}
                {task.children && task.children.length > 0 && (
                    <div className="d-flex flex-column gap-1 mt-2">
                        {task.children.map(child => (
                            <div key={child.id} className="subtask-badge">
                                <div
                                    className={`rounded-circle border ${child.is_completed ? 'bg-success border-success' : 'border-secondary'}`}
                                    style={{ width: 12, height: 12 }}
                                    onClick={() => {
                                        const newIsCompleted = !child.is_completed;
                                        const statusId = getStatusId(newIsCompleted ? 'TO_COMPLETED' : 'TO_OPEN');
                                        onUpdateTask(child.id, { isCompleted: newIsCompleted, statusId });
                                    }}
                                ></div>
                                <span className={child.is_completed ? 'text-decoration-line-through text-muted' : ''}>{child.content}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="task-footer">
                <div className="d-flex align-items-center justify-content-between w-100">
                    <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()} style={{ cursor: 'pointer' }}>
                        <OverlayTrigger trigger="click" placement="bottom" overlay={<TaskDueDatePopover task={task} onUpdate={handleUpdate} />} rootClose popperConfig={{ strategy: 'fixed' }}>
                            {task.due_date ? (
                                <div className={`d-flex align-items-center gap-1 ${new Date(task.due_date) < new Date().setHours(0, 0, 0, 0) ? 'text-danger' : 'text-muted'}`}>
                                    <CalendarIcon size={12} />
                                    <span className="small">{new Date(task.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                                    {task.recurrence_rule && <span className="small text-muted fst-italic" style={{ fontSize: '0.7em' }}>({getRecurrenceLabel(task.recurrence_rule)})</span>}
                                </div>
                            ) : (
                                <div className="d-flex align-items-center gap-1 text-muted opacity-50 hover-opacity-100">
                                    <CalendarIcon size={12} />
                                    <span className="small">Date</span>
                                </div>
                            )}
                        </OverlayTrigger>
                    </div>

                    {/* Assignee Card View */}
                    <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
                        {renderAssigneeTrigger()}
                    </div>
                </div>
            </div>
            {!completed && (
                <div className="px-3 pb-2 d-flex justify-content-between align-items-center">
                    {/* Status Trigger Card View - Bottom Left */}
                    <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
                        <OverlayTrigger trigger="click" placement="bottom" overlay={<TaskStatusPopover task={task} taskStatuses={taskStatuses} onUpdate={handleUpdate} />} rootClose popperConfig={{ strategy: 'fixed' }}>
                            <Badge bg="light" text="dark" className="border d-flex align-items-center gap-1 cursor-pointer fw-normal">
                                {(() => {
                                    const statusObj = taskStatuses.find(s => s.id === task.status_id);
                                    const statusName = statusObj ? statusObj.name : (task.status_name || 'Not Started');
                                    const color = statusObj ? statusObj.color : 'text-muted';

                                    const upper = statusName.toUpperCase().replace(' ', '_');
                                    const config = STATUS_CONFIG[upper] || STATUS_CONFIG['NOT_STARTED'];
                                    const Icon = config.icon;
                                    return (
                                        <>
                                            <Icon size={12} className={color} />
                                            <span>{statusName}</span>
                                        </>
                                    );
                                })()}
                            </Badge>
                        </OverlayTrigger>
                    </div>

                    <div className="d-flex gap-1">
                        {/* Reminder Trigger Card */}
                        <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
                            <OverlayTrigger trigger="click" placement="bottom" overlay={<TaskReminderPopover task={task} onAddReminder={onAddReminder} onDeleteReminder={onDeleteReminder} />} rootClose popperConfig={{ strategy: 'fixed' }}>
                                <Button
                                    variant="link"
                                    className={`p-0 ${task.reminders?.length > 0 ? 'text-primary' : 'text-muted'} opacity-50 hover-opacity-100`}
                                    title="Set Reminder"
                                >
                                    <Bell size={16} fill={task.reminders?.length > 0 ? 'currentColor' : 'none'} />
                                </Button>
                            </OverlayTrigger>
                        </div>

                        {/* Recurrence Trigger Card */}
                        <div onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
                            <OverlayTrigger trigger="click" placement="bottom" overlay={<TaskRecurrencePopover task={task} onUpdate={handleUpdate} />} rootClose popperConfig={{ strategy: 'fixed' }}>
                                <Button
                                    variant="link"
                                    className={`p-0 ${task.recurrence_rule ? 'text-primary' : 'text-muted'} opacity-50 hover-opacity-100`}
                                    title="Set Recurrence"
                                >
                                    <Repeat size={16} />
                                </Button>
                            </OverlayTrigger>
                        </div>

                        <Button
                            variant="link"
                            className="p-0 text-muted"
                            onClick={(e) => { e.stopPropagation(); onCreateSubtask(task.id); }}
                            onPointerDown={(e) => e.stopPropagation()}
                            title="Add Subtask"
                        >
                            <Plus size={16} />
                        </Button>

                        {/* Delete Button Card View */}
                        <Button
                            variant="link"
                            className="p-0 text-muted opacity-50 hover-danger"
                            onClick={handleDeleteClick}
                            onPointerDown={(e) => e.stopPropagation()}
                            title="Delete Task"
                        >
                            <Trash2 size={16} />
                        </Button>
                    </div>
                </div>
            )}

            <DeleteRecurringTaskDialog
                show={showDeleteDialog}
                onHide={() => setShowDeleteDialog(false)}
                onDeleteInstance={handleDeleteInstance}
                onDeleteSeries={handleDeleteSeries}
            />
        </div>
    );
};
